import React from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LabelList, PieChart, Pie, Cell,
} from 'recharts';

export const SERIES = [
  { key: 'enrollments', name: 'Enrollments', color: '#2563eb' },
  { key: 'completions', name: 'Completions', color: '#16a34a' },
  { key: 'vouchers', name: 'Vouchers Issued', color: '#f59e0b' },
];
const DONUT = ['#194449', '#f59e0b', '#2563eb', '#a855f7'];
const n = v => (v ? Number(v).toLocaleString() : '');
const tick = { fontSize: 11, fill: '#475569' };

export function ChartCard({ title, testId, children, height = 300, span = 1 }) {
  return (
    <div className="stat-card" data-testid={testId} style={{ padding: 16, gridColumn: `span ${span}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>{title}</div>
      <div style={{ height }}>{children}</div>
    </div>
  );
}

const Empty = () => <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: '#94a3b8', fontSize: 13 }}>No data for this selection</div>;

export function TrendChart({ data, keys = SERIES }) {
  if (!data?.length) return <Empty />;
  if (data.length < 2) return <BarsChart data={data} keys={keys} />;
  return (
    <ResponsiveContainer>
      <LineChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" tick={tick} />
        <YAxis tick={tick} />
        <Tooltip formatter={v => Number(v).toLocaleString()} itemSorter={null} />
        <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
        {keys.map((s, i) => (
          <Line key={s.key} type="linear" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={{ r: 3 }}>
            <LabelList dataKey={s.key} position={i === keys.length - 1 ? 'bottom' : 'top'} offset={8} formatter={n} style={{ fontSize: 10, fill: s.color }} />
          </Line>
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function BarsChart({ data, keys = SERIES, horizontal = false, labelWidth = 140, onBarClick }) {
  if (!data?.length || !data.some(d => keys.some(k => d[k.key]))) return <Empty />;
  return (
    <ResponsiveContainer>
      <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'} margin={{ top: 20, right: 40, left: 0, bottom: 0 }} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        {horizontal ? (
          <><XAxis type="number" tick={tick} /><YAxis type="category" dataKey="label" width={labelWidth} tick={{ ...tick, fontSize: 10 }} interval={0} /></>
        ) : (
          <><XAxis dataKey="label" tick={tick} interval={0} /><YAxis tick={tick} /></>
        )}
        <Tooltip formatter={v => Number(v).toLocaleString()} labelFormatter={(l, p) => p?.[0]?.payload?.name || l} itemSorter={null} />
        {keys.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />}
        {keys.map(s => (
          <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
            onClick={onBarClick ? (d => onBarClick(d?.payload || d)) : undefined} style={onBarClick ? { cursor: 'pointer' } : undefined}>
            <LabelList dataKey={s.key} position={horizontal ? 'right' : 'top'} formatter={n} style={{ fontSize: 10, fill: '#334155' }} />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DonutChart({ data }) {
  if (!data?.length) return <Empty />;
  return (
    <ResponsiveContainer>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="label" innerRadius="52%" outerRadius="78%" paddingAngle={2}
          label={({ label, value, pct }) => `${label}: ${Number(value).toLocaleString()} (${pct}%)`} labelLine style={{ fontSize: 12 }}>
          {data.map((d, i) => <Cell key={d.label} fill={DONUT[i % DONUT.length]} />)}
        </Pie>
        <Tooltip formatter={v => Number(v).toLocaleString()} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}
